import { useId } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Lozenge from '@atlaskit/lozenge';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { getConnectionStatusConfig } from '@/common/utils/get-connection-status-config';

import { messages } from './messages';

import type {
	FastshiftDcConnectionStatus,
	FastshiftDcProductType,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

export type InstanceSummarySectionProps = {
	isLoading: boolean;
	productType?: FastshiftDcProductType | null;
	version?: string | null;
	connectionStatus?: FastshiftDcConnectionStatus | null;
	serverId?: string | null;
};

const getDisplayValue = (value: string | null | undefined, fallback: string) =>
	value?.trim() || fallback;

const styles = cssMap({
	card: {
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.large'),
		paddingBlock: token('space.300'),
		paddingInline: token('space.300'),
	},
	table: {
		width: '100%',
		borderCollapse: 'collapse',
	},
	headerCell: {
		width: '25%',
		textAlign: 'left',
		paddingInlineEnd: token('space.400'),
		paddingBlockEnd: token('space.100'),
	},
	cell: {
		width: '25%',
		textAlign: 'left',
		paddingInlineEnd: token('space.400'),
	},
});

export const InstanceSummarySection = ({
	isLoading,
	productType,
	version,
	connectionStatus,
	serverId,
}: InstanceSummarySectionProps) => {
	const { formatMessage } = useIntl();
	const headingId = useId();

	const connectionStatusConfig = connectionStatus?.trim()
		? getConnectionStatusConfig(connectionStatus)
		: null;
	const emptyValue = formatMessage(messages.emptyValue);

	const columns = [
		{
			key: 'product',
			label: messages.productLabel,
			content: getDisplayValue(productType, emptyValue),
		},
		{
			key: 'version',
			label: messages.versionLabel,
			content: getDisplayValue(version, emptyValue),
		},
		{
			key: 'connection',
			label: messages.connectionLabel,
			content: connectionStatusConfig ? (
				<Lozenge appearance={connectionStatusConfig.appearance}>
					{formatMessage(connectionStatusConfig.message)}
				</Lozenge>
			) : (
				emptyValue
			),
		},
		{
			key: 'serverId',
			label: messages.serverIdLabel,
			content: getDisplayValue(serverId, emptyValue),
		},
	] as const;

	return (
		<Box as="section" xcss={styles.card} aria-labelledby={headingId} aria-busy={isLoading}>
			{isLoading && (
				<VisuallyHidden role="status">{formatMessage(messages.loadingLabel)}</VisuallyHidden>
			)}
			<Stack space="space.150">
				<Heading as="h2" size="small" id={headingId}>
					{formatMessage(messages.sectionTitle)}
				</Heading>
				<Box
					as="table"
					xcss={styles.table}
					testId={isLoading ? 'instance-summary-loading' : undefined}
				>
					<Box as="thead">
						<Box as="tr">
							{columns.map(({ key, label }) => (
								<Box as="th" key={key} xcss={styles.headerCell}>
									<Text size="small" color="color.text.subtle">
										{formatMessage(label)}
									</Text>
								</Box>
							))}
						</Box>
					</Box>
					<Box as="tbody">
						<Box as="tr">
							{columns.map(({ key, content }) =>
								isLoading ? (
									<Box as="td" key={key} xcss={styles.cell}>
										<Skeleton
											width="80px"
											height="36px"
											isShimmering
											testId={`instance-summary-${key}-skeleton`}
										/>
									</Box>
								) : (
									<Box as="td" key={key} xcss={styles.cell}>
										{typeof content === 'string' ? <Text>{content}</Text> : content}
									</Box>
								),
							)}
						</Box>
					</Box>
				</Box>
			</Stack>
		</Box>
	);
};
